import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "./Admin";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, RefreshCw } from "lucide-react";

type Studio = { id: string; name: string };
type BlockedRow = { start_at: string; end_at: string; source: string; source_id: string; reason: string | null };
type Recon = { stripe_event_id: string; event_type: string; created_at: string; source: string; deltas: any };

const sourceVariant = (s: string): "default" | "secondary" | "outline" | "destructive" => {
  if (s === "booking") return "default";
  if (s === "calendar_block") return "secondary";
  if (s === "lock") return "outline";
  return "outline";
};

const AdminSlotBlockAudit = () => {
  const [studios, setStudios] = useState<Studio[]>([]);
  const [studioId, setStudioId] = useState("");
  const [rangeDays, setRangeDays] = useState(14);
  const [rows, setRows] = useState<BlockedRow[]>([]);
  const [recons, setRecons] = useState<Record<string, Recon[]>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from("studios").select("id,name").eq("active", true).order("display_order")
      .then(({ data }) => {
        const list = (data ?? []) as Studio[];
        setStudios(list);
        if (list.length && !studioId) setStudioId(list[0].id);
      });
  }, []);

  const load = async () => {
    if (!studioId) return;
    setLoading(true);
    const from = new Date(); from.setDate(from.getDate() - rangeDays);
    const to = new Date(); to.setDate(to.getDate() + rangeDays);
    const { data } = await supabase.rpc("get_blocked_slots_v2" as any, {
      _studio_id: studioId, _from: from.toISOString(), _to: to.toISOString(),
    });
    const blocked = ((data as any[]) ?? []) as BlockedRow[];
    blocked.sort((a, b) => new Date(b.start_at).getTime() - new Date(a.start_at).getTime());
    setRows(blocked);
    // Pull reconciliations for bookings (to surface webhook event ids)
    const bookingIds = blocked.filter((r) => r.source === "booking").map((r) => r.source_id);
    if (bookingIds.length) {
      const { data: rec } = await supabase
        .from("booking_reconciliations").select("booking_id,stripe_event_id,event_type,created_at,source,deltas")
        .in("booking_id", bookingIds).order("created_at", { ascending: false });
      const grouped: Record<string, Recon[]> = {};
      (rec ?? []).forEach((r: any) => {
        (grouped[r.booking_id] ??= []).push(r);
      });
      setRecons(grouped);
    } else { setRecons({}); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [studioId, rangeDays]);

  useEffect(() => {
    if (!studioId) return;
    const ch = supabase.channel(`slot_audit_${studioId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "calendar_blocks" }, () => load())
      .subscribe();
    // bookings + slot_locks are no longer broadcast via realtime (privacy); poll instead.
    const id = window.setInterval(() => { load(); }, 15000);
    return () => { supabase.removeChannel(ch); window.clearInterval(id); };
  }, [studioId]);

  const summary = useMemo(() => {
    const c = { booking: 0, calendar_block: 0, lock: 0 } as Record<string, number>;
    rows.forEach((r) => { c[r.source] = (c[r.source] ?? 0) + 1; });
    return c;
  }, [rows]);

  return (
    <AdminLayout title="SLOT BLOCK AUDIT">
      <p className="font-body text-muted-foreground mb-6 max-w-2xl">
        Every reason a time slot is currently unavailable for this studio — including the
        Stripe webhook event that confirmed it (payment, refund, retry) or the manual block
        that created it.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Select value={studioId} onValueChange={setStudioId}>
          <SelectTrigger className="w-64"><SelectValue placeholder="Studio…" /></SelectTrigger>
          <SelectContent>{studios.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(rangeDays)} onValueChange={(v) => setRangeDays(Number(v))}>
          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">±7 days</SelectItem>
            <SelectItem value="14">±14 days</SelectItem>
            <SelectItem value="30">±30 days</SelectItem>
            <SelectItem value="90">±90 days</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} className="gap-2"><RefreshCw className="w-4 h-4" /> Refresh</Button>
        <div className="ml-auto flex gap-2 text-xs font-mono">
          <Badge variant="default">{summary.booking ?? 0} bookings</Badge>
          <Badge variant="secondary">{summary.calendar_block ?? 0} blocks</Badge>
          <Badge variant="outline">{summary.lock ?? 0} active locks</Badge>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground font-body text-sm">No blocked slots in this range.</p>
      ) : (
        <div className="bg-panel border border-border divide-y divide-border">
          {rows.map((r) => {
            const linked = r.source === "booking" ? recons[r.source_id] ?? [] : [];
            return (
              <div key={`${r.source}-${r.source_id}-${r.start_at}`} className="p-4">
                <div className="flex flex-wrap items-baseline gap-3">
                  <Badge variant={sourceVariant(r.source)}>{r.source}</Badge>
                  <span className="font-mono text-xs">
                    {new Date(r.start_at).toLocaleString()} → {new Date(r.end_at).toLocaleTimeString()}
                  </span>
                  {r.reason && <span className="text-xs text-muted-foreground">{r.reason}</span>}
                  <span className="ml-auto text-[10px] font-mono text-muted-foreground">{r.source_id.slice(0, 8)}</span>
                  {r.source === "booking" && (
                    <Button asChild size="sm" variant="outline" className="h-7 text-xs">
                      <Link to={`/admin/bookings/${r.source_id}`}>Open booking</Link>
                    </Button>
                  )}
                </div>
                {linked.length > 0 && (
                  <ul className="mt-3 space-y-1 border-l-2 border-border pl-3">
                    {linked.map((rc, i) => (
                      <li key={i} className="text-xs font-mono">
                        <span className="text-primary">{rc.event_type}</span>
                        <span className="text-muted-foreground"> · {new Date(rc.created_at).toLocaleString()}</span>
                        <span className="text-muted-foreground"> · {rc.source}</span>
                        <div className="text-[10px] text-muted-foreground break-all">{rc.stripe_event_id}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminSlotBlockAudit;